/**
 * @module lib/commercial/keytar/cannabis-sponsored-content/template
 */
import tgamConfig from "config/tgam";
import Template, { BLOCKOUT_TYPES } from "lib/commercial/keytar/Template";
import { removeRootElements, setupContainer, setupMain } from "lib/commercial/keytar/common/takeover";
import ee from "lib/eventemitter";
import { uNoScrollClass } from "ui/site-header";
import KeyboardTrap from "utils/keyboard-trap";
import { qs, getById } from "utils/utils";
import displaySteps, { getMarkup } from "./pages";
import logger from "utils/logger";

const debugTag = "[KEYTAR]";

/** @type {String} */
const blockoutType = tgamConfig.meta.isStoryInteractivePage
  ? BLOCKOUT_TYPES.EVERYTHING // responsive modal displayed
  : BLOCKOUT_TYPES.HEADER_NAV_ACCESSIBLE; // responsive modal displayed

/**
 * @type {Boolean}
 * @todo Even though the "translucentBackground" value is non-configurable,
 * we're preserving the logic that relies on it (see below) because we're
 * likely going to need it in the future. We'll refactor/move it in an upcoming
 * tech debt ticket.
 */
const translucentBackground = false;

const props = {
  blockoutType,
  /**
   * @method render
   * @description Always render the paywall template.
   * @returns {Promise<Boolean>} true if rendered
   */
  render() {
    if (translucentBackground) {
      // Remove skip link ID from article headline because
      // Keytar adds the same ID to the wall container
      getById("skip-link-target").id = "";
      document.body.classList.add(uNoScrollClass);
    } else {
      // No need to alter the skip link target's ID because
      // the entire element will be removed from the DOM
      if (!tgamConfig.meta.isStoryInteractivePage) {
        removeRootElements(["top-furniture-area"]);
      } else {
        removeRootElements([]);
      }
    }

    const klasses = !tgamConfig.meta.isStoryInteractivePage
      ? ["header-nav", "cannabis-sponsored"]
      : ["cannabis-sponsored"]; // no header

    setupContainer(klasses); // See: resources/styles/keytar/keytar.scss

    const modalEl = setupMain(["cannabis-sponsored"]);

    modalEl.innerHTML = `
      <div class="arc-c-cannabis-age-gate">
        <div class="arc-c-cannabis-age-gate__links">
          <a href="${tgamConfig.env.baseHomepageUrl}" id="js-arc-c-cannabis-age-gate-close" class="c-button c-button--icon-right c-button--no-border c-button--no-border-radius c-button--transparent-lighten">
            <span class="c-button__text">Close <span class="u-visually-hidden">modal. (You will be redirected back to the homepage.)</span></span>
            <svg class="c-button__icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
              <use xlink:href="${tgamConfig.env.svgSpritePath}#close-x"></use>
            </svg>
          </a>
        </div>
        <div class="arc-c-cannabis-age-gate__content">
          <div class="arc-c-cannabis-age-gate__content-body">
            ${getMarkup()}
          </div>
        </div>
      </div>
    `;

    displaySteps();

    // Track when user does not pass Age Gate wall (i.e., modal's Exit/close link is clicked)
    document.addEventListener("click", e => {
      if (!e.target.matches("#js-arc-c-cannabis-age-gate-close")) {
        return;
      }
      ee.trigger("analytics.trackGTMEvent", [{
        action: "close window: back to home"
      }, "ageGateEvent"]);
      logger.info(`${debugTag} Modal's X button back to home clicked`);
    }, false);

    // If the background is solid, then there's no need to create a keyboard
    // trap because the elements behind the wall will have been removed
    if (translucentBackground) {
      const keyboardTrap = new KeyboardTrap([
        {
          item: qs(".c-skip-link--primary"),
          type: "element"
        },
        {
          item: getById("top-furniture-area"),
          type: "container"
        },
        {
          item: modalEl,
          type: "container"
        }
      ]);

      keyboardTrap.activate();

      // Since the menu panel creates its own keyboard trap, this trap will
      // be deactivated when the panel opens. Later, when the panel has closed,
      // we'll need to reactivate this trap again.
      ee.on("menu-panel:closed", () => {
        keyboardTrap.activate();
      });
    }
    return Promise.resolve(true);
  }
};

export default new Template(props);
